import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Simple types for our backend API
interface Subscription {
  id: string;
  userId: string;
  tier: 'FREE' | 'PROFESSIONAL' | 'ENTERPRISE';
  status: 'ACTIVE' | 'TRIALING' | 'PAST_DUE' | 'CANCELLED' | 'UNPAID';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  trialEnd?: string;
  createdAt: string;
  updatedAt: string;
}

interface UsageTracking {
  id: string;
  userId: string;
  period: string;
  recipesCreated: number;
  waterProfilesCreated: number;
  formulationsCreated: number;
  customSaltsCreated: number;
  storageUsedMB: number;
  apiCalls: number;
  lastUpdated: string;
}

interface TierLimits {
  recipesPerMonth: number;
  waterProfilesTotal: number;
  formulationsTotal: number;
  customSaltsTotal: number;
  storageMaxMB: number;
  apiCallsPerDay: number;
}

// Import API configuration
import { API_BASE_URL } from '../../config/api';

interface SubscriptionState {
  subscription: Subscription | null;
  usage: UsageTracking | null;
  limits: TierLimits | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: SubscriptionState = {
  subscription: null,
  usage: null,
  limits: null,
  isLoading: false,
  error: null
};

// Helper function to get auth token
const getAuthToken = (getState: any): string | null => {
  const state = getState();
  return state.auth?.token || null;
};

// Async thunks
export const fetchSubscription = createAsyncThunk(
  'subscription/fetchSubscription',
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(result.error || 'Failed to fetch subscription');
      }

      return result.data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

export const fetchUsage = createAsyncThunk(
  'subscription/fetchUsage',
  async (period: string | undefined, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/user/usage`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(result.error || 'Failed to fetch usage');
      }

      return result.data.usage;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

export const startTrial = createAsyncThunk(
  'subscription/startTrial',
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/subscription/start-trial`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(result.error || 'Failed to start trial');
      }

      return result.data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

export const checkUsageLimit = createAsyncThunk(
  'subscription/checkUsageLimit',
  async (resourceType: string, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/subscription/check-limit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ action: resourceType }),
      });

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(result.error || 'Failed to check usage limit');
      }

      return result.data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

export const checkFeatureAccess = createAsyncThunk(
  'subscription/checkFeatureAccess',
  async (feature: string, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/subscription/check-feature`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ feature }),
      });

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(result.error || 'Failed to check feature access');
      }

      return result.data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

export const initiateUpgrade = createAsyncThunk(
  'subscription/initiateUpgrade',
  async (upgradeData: { targetTier: string }, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/subscription/upgrade`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(upgradeData),
      });

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(result.error || 'Failed to initiate upgrade');
      }

      return result.data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'subscription/cancelSubscription',
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(result.error || 'Failed to cancel subscription');
      }

      return result.data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

export const trackUsage = createAsyncThunk(
  'subscription/trackUsage',
  async (usageData: { action: string; amount?: number }, { rejectWithValue, getState }) => {
    try {
      const token = getAuthToken(getState);
      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/subscription/track-usage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(usageData),
      });

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue(result.error || 'Failed to track usage');
      }

      return result.data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

// Subscription slice
const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateUsage: (state, action: PayloadAction<Partial<UsageTracking>>) => {
      if (state.usage) {
        state.usage = { ...state.usage, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch subscription
    builder
      .addCase(fetchSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscription = action.payload.subscription;
        state.usage = action.payload.usage;
        state.limits = action.payload.limits;
        state.error = null;
      })
      .addCase(fetchSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch usage
    builder
      .addCase(fetchUsage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.usage = action.payload;
        state.error = null;
      })
      .addCase(fetchUsage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Start trial
    builder
      .addCase(startTrial.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(startTrial.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscription = action.payload.subscription;
        state.error = null;
      })
      .addCase(startTrial.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Cancel subscription
    builder
      .addCase(cancelSubscription.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.subscription) {
          state.subscription.cancelAtPeriodEnd = true;
        }
        state.error = null;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Track usage
    builder
      .addCase(trackUsage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(trackUsage.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.usage) {
          state.usage = action.payload.usage;
        }
        state.error = null;
      })
      .addCase(trackUsage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, updateUsage } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
